import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaSave, FaUser, FaLock, FaUserPlus } from 'react-icons/fa';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { createNewAdmin, getCurrentUser, updateUserPassword } from '../../Functions/AdminAuth';

const SettingsCard = styled(motion.div)`
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0,0,0,${props => props.darkMode ? '0.2' : '0.05'});
  background: ${props => props.darkMode ? '#2d2d2d' : 'white'};
  margin-bottom: 20px;
`;

const SettingsSection = styled.div`
  margin-bottom: 20px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h5`
  font-weight: 600;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${props => props.darkMode ? '#f5f5f5' : 'inherit'};
`;

const Settings = ({ darkMode }) => {
  const [formData, setFormData] = useState({
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // New state for admin creation
  const [newAdminData, setNewAdminData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
    // Removed role field
  });
  
  const [isCreatingAdmin, setIsCreatingAdmin] = useState(false);
  
  // Fetch current user email on component mount
  useEffect(() => {
    const currentUser = getCurrentUser();
    if (currentUser && currentUser.email) {
      setFormData(prevState => ({
        ...prevState,
        email: currentUser.email
      }));
    }
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle changes in the new admin form
  const handleAdminFormChange = (e) => {
    const { name, value } = e.target;
    setNewAdminData({
      ...newAdminData,
      [name]: value
    });
  };
  
  // Handle creating a new admin user
  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setIsCreatingAdmin(true);
    
    // Form validation
    if (!newAdminData.email || !newAdminData.password) {
      toast.error("Email and password are required");
      setIsCreatingAdmin(false);
      return;
    }
    
    if (newAdminData.password !== newAdminData.confirmPassword) {
      toast.error("Passwords don't match");
      setIsCreatingAdmin(false);
      return;
    }
    
    if (newAdminData.password.length < 6) {
      toast.error("Password should be at least 6 characters");
      setIsCreatingAdmin(false);
      return;
    }
    
    try {
      const result = await createNewAdmin(newAdminData.email, newAdminData.password);
      
      if (result.success) {
        toast.success("New admin account created successfully!");
        // Reset the form
        setNewAdminData({
          email: '',
          password: '',
          confirmPassword: ''
        });
      } else {
        toast.error(result.error || "Failed to create admin account");
      }
    } catch (error) {
      toast.error("Error creating admin account");
      console.error(error);
    } finally {
      setIsCreatingAdmin(false);
    }
  };
  
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    
    // Validate password if being changed
    if (formData.newPassword) {
      if (formData.newPassword !== formData.confirmPassword) {
        toast.error("New passwords don't match");
        return;
      }
      
      if (!formData.currentPassword) {
        toast.error("Please enter your current password");
        return;
      }
      
      // Add loading state for better UX
      const btn = e.target.querySelector('button[type="submit"]');
      btn.disabled = true;
      btn.innerHTML = '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Updating...';
      
      try {
        // Call the new function to update password
        const result = await updateUserPassword(
          formData.currentPassword, 
          formData.newPassword
        );
        
        if (result.success) {
          toast.success("Password updated successfully");
          // Reset form fields
          setFormData(prevState => ({
            ...prevState,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          }));
        } else {
          toast.error(result.error || "Failed to update password");
        }
      } catch (error) {
        console.error(error);
        toast.error("An error occurred while updating password");
      } finally {
        // Reset button state
        btn.disabled = false;
        btn.innerHTML = '<i class="fas fa-save me-2"></i> Save Settings';
      }
    } else {
      toast.info("No changes to save");
    }
  };
  
  return (
    <div className="container-fluid px-4">
      <motion.h1 
        className="mt-4 mb-4 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        Settings
      </motion.h1>
      
      <div className="row">
        <div className="col-lg-6">
          <SettingsCard
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            darkMode={darkMode}
          >
            <form onSubmit={handleSaveSettings}>
              <SettingsSection>
                <SectionTitle darkMode={darkMode} className="text-center"><FaUser /> Profile Information</SectionTitle>
                <div className="mb-3">
                  <label className="form-label">Email Address</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    readOnly
                  />
                  <small className="form-text text-muted">
                    This is your current admin email
                  </small>
                </div>
              </SettingsSection>

              <SettingsSection>
                <SectionTitle darkMode={darkMode} className="text-center"><FaLock /> Change Password</SectionTitle>
                <div className="mb-3">
                  <label className="form-label">Current Password</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleChange}
                    placeholder="Enter current password"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">New Password</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="Enter new password"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Confirm New Password</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm new password"
                  />
                </div>
              </SettingsSection>
              
              <div className="text-center">
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="submit" 
                  className="btn btn-primary px-4 mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                >
                  <FaSave className="me-2" /> Save Settings
                </motion.button>
              </div>
            </form>
          </SettingsCard>
        </div>
        
        <div className="col-lg-6">
          <SettingsCard
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            darkMode={darkMode}
          >
            <SettingsSection>
              <SectionTitle darkMode={darkMode} className="text-center"><FaUserPlus /> Create New Admin</SectionTitle>
              <form onSubmit={handleCreateAdmin}>
                <div className="mb-3">
                  <label className="form-label">Email Address</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    name="email"
                    value={newAdminData.email}
                    onChange={handleAdminFormChange}
                    placeholder="Enter email address"
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Password</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    name="password"
                    value={newAdminData.password}
                    onChange={handleAdminFormChange}
                    placeholder="Enter password"
                    required
                  />
                  <small className="form-text text-muted">Password must be at least 6 characters long</small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Confirm Password</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    name="confirmPassword"
                    value={newAdminData.confirmPassword}
                    onChange={handleAdminFormChange}
                    placeholder="Confirm password"
                    required
                  />
                </div>
                <div className="text-center">
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="submit" 
                    className="btn btn-success px-4"
                    disabled={isCreatingAdmin}
                  >
                    <FaUserPlus className="me-2" /> 
                    {isCreatingAdmin ? 'Creating...' : 'Create Admin Account'}
                  </motion.button>
                </div>
              </form>
            </SettingsSection>
          </SettingsCard>
        </div>
      </div>
    </div>
  );
};

export default Settings;
